import React from "react";
import { Outlet, ScrollRestoration, useLocation } from "react-router-dom";
import Header from "../home/Header/Header";
import Footer from "../home/Footer/FooterNew";
import ScrollToTop from "../ScrollToTop";
import BottomNav from "../home/BottomNav/BottomNav";
import { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";

const Layout = () => {
  const location = useLocation();
  const [showBottomNav, setShowBottomNav] = useState(false);

  // State to track if modals are open
  const [isLensTypeModalOpen, setIsLensTypeModalOpen] = useState(false);
  const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);

  // Hide bottom nav only for specific routes and modals
  useEffect(() => {
    // These are routes where bottom nav should always be hidden
    const hideOnRoutes = [
      '/login',
      '/register',
      '/forgot-password',
      '/reset-password',
      '/admin',
      '/dashboard',
    ];
    
    // Check if current route should hide bottom nav
    const shouldHideRoute = hideOnRoutes.some(route => 
      location.pathname.startsWith(route)
    );
    
    // Check if any modal or drawer is open that should hide the bottom nav
    const shouldHideForModal = () => {
      // Always hide when LensType or CouponModal is open
      if (isLensTypeModalOpen || isCouponModalOpen) return true;
      
      // Check for other full-screen modals
      const modal = document.querySelector('.modal, .drawer, [role="dialog"]');
      if (!modal) return false;
      
      // Check if this is a full-screen modal/popup that should hide the bottom nav
      const modalRect = modal.getBoundingClientRect();
      const isFullScreenModal = 
        modalRect.width >= window.innerWidth * 0.9 || 
        modalRect.height >= window.innerHeight * 0.7;
      
      return isFullScreenModal;
    };
    
    // Show on mobile devices only
    const isMobile = window.innerWidth < 1024;
    const shouldShowBottomNav = isMobile && !shouldHideRoute && !shouldHideForModal();
    
    setShowBottomNav(shouldShowBottomNav);
    
    // Add/remove padding to main content based on bottom nav visibility
    const mainContent = document.querySelector('main');
    if (mainContent) {
      if (shouldShowBottomNav) {
        mainContent.classList.add('pb-24'); // Add padding to prevent content from being hidden
      } else {
        mainContent.classList.remove('pb-24');
      }
    }
    
    // Add click handler to close modals when clicking outside
    const handleClickOutside = (e) => {
      const modal = document.querySelector('.modal, [role="dialog"]');
      if (modal && !modal.contains(e.target) && !e.target.closest('.modal, [role="dialog"]')) {
        const closeButton = modal.querySelector('.modal-close, [data-close-modal]');
        if (closeButton) closeButton.click();
      }
    };
    
    // Listen for modal state changes
    const handleLensTypeModalState = (e) => {
      setIsLensTypeModalOpen(e.detail.isOpen);
    };

    const handleCouponModalState = (e) => {
      setIsCouponModalOpen(e.detail.isOpen);
    };
    
    // Add event listeners
    window.addEventListener('lensTypeModalState', handleLensTypeModalState);
    window.addEventListener('couponModalState', handleCouponModalState);
    document.addEventListener('mousedown', handleClickOutside);
    
    // Cleanup
    return () => {
      window.removeEventListener('lensTypeModalState', handleLensTypeModalState);
      window.removeEventListener('couponModalState', handleCouponModalState);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [location, isLensTypeModalOpen, isCouponModalOpen]); // Include all dependencies

  // Add class to body when bottom nav is visible
  useEffect(() => {
    if (showBottomNav) {
      document.body.classList.add('has-bottom-nav');
    } else {
      document.body.classList.remove('has-bottom-nav');
    }
    
    return () => {
      document.body.classList.remove('has-bottom-nav');
    };
  }, [showBottomNav]);

  return (
    <>
      <ScrollToTop />
      <Header />
      <main className="min-h-screen pb-20 lg:pb-0">
        <Outlet />
      </main>
      <Footer />
      <AnimatePresence>
        {showBottomNav && <BottomNav key="bottom-nav" />}
      </AnimatePresence>
      <ScrollRestoration />
    </>
  );
};

export default Layout;